/**
 * OS Targeting form — the per-campaign operator UI.
 *
 * Renders:
 *   - campaign id input
 *   - allow / deny mode toggle
 *   - iOS / Android / Windows / macOS selection chips
 *   - optional minimum-version input
 *   - save + reset actions
 *
 * Data layer: TanStack Query for fetch + mutation, Sonner for feedback.
 * Form layer: local state + hand-rolled validator in `./api.ts` that mirrors
 * a Zod-style `safeParse` surface. React-Hook-Form would pull an extra dep
 * that the root has locked out of package.json — the manual binding keeps
 * the lane to the three owned files only.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	apiListToFormSelection,
	deleteOsTargeting,
	fetchOsTargeting,
	FORM_OS_CODES,
	type FormOsCode,
	osTargetingFormSchema,
	osTargetingQueryKeys,
	OS_LABELS,
	saveOsTargeting,
	type TargetingMode,
} from "./api";

// ---------------------------------------------------------------------------
// Local UI state — one typed shape, reducer-free; the surface is small enough
// that explicit setters are clearer than a reducer.
// ---------------------------------------------------------------------------

interface FormState {
	campaignId: string;
	mode: TargetingMode;
	selected: FormOsCode[];
	minVersion: string;
}

const EMPTY_FORM: FormState = {
	campaignId: "",
	mode: "allow",
	selected: [],
	minVersion: "",
};

// ---------------------------------------------------------------------------
// Static style tokens — keeping classnames grouped so the JSX is readable.
// Light theme only, zinc borders, 6–8px rounded, emerald→sky gradient used
// only on the headline per brand rules.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";

export interface OsTargetingFormProps {
	/** When provided, prefills and locks the campaign id field. */
	initialCampaignId?: string;
}

export function OsTargetingForm({ initialCampaignId }: OsTargetingFormProps) {
	const queryClient = useQueryClient();

	const [form, setForm] = useState<FormState>({
		...EMPTY_FORM,
		campaignId: initialCampaignId ?? "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [hydratedFor, setHydratedFor] = useState<string | null>(null);

	const trimmedCampaignId = form.campaignId.trim();
	const canQuery = trimmedCampaignId.length > 0;

	// ---------------- Load existing row (if any) ----------------
	const query = useQuery({
		queryKey: osTargetingQueryKeys.byCampaign(trimmedCampaignId),
		queryFn: () => fetchOsTargeting(trimmedCampaignId),
		enabled: canQuery,
	});

	useEffect(() => {
		if (!canQuery) {
			setHydratedFor(null);
			return;
		}
		if (query.isSuccess && hydratedFor !== trimmedCampaignId) {
			const row = query.data;
			if (row) {
				const { mode, selected } = apiListToFormSelection(row.os_list);
				setForm((prev) => ({
					...prev,
					campaignId: trimmedCampaignId,
					mode,
					selected,
				}));
			} else {
				setForm((prev) => ({
					...prev,
					mode: "allow",
					selected: [],
				}));
			}
			setHydratedFor(trimmedCampaignId);
			setErrors({});
		}
	}, [query.isSuccess, query.data, trimmedCampaignId, hydratedFor, canQuery]);

	// ---------------- Mutations ----------------
	const saveMutation = useMutation({
		mutationFn: saveOsTargeting,
		onSuccess: (row) => {
			toast.success("OS targeting saved.");
			queryClient.setQueryData(
				osTargetingQueryKeys.byCampaign(row.campaign_id),
				row,
			);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to save OS targeting.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (campaignId: string) => deleteOsTargeting(campaignId),
		onSuccess: (_void, campaignId) => {
			toast.success("OS targeting cleared.");
			queryClient.setQueryData(
				osTargetingQueryKeys.byCampaign(campaignId),
				null,
			);
			setForm((prev) => ({ ...prev, selected: [], mode: "allow" }));
			setHydratedFor(campaignId);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to clear OS targeting.");
		},
	});

	// ---------------- Derived UI data ----------------
	const modeDescription = useMemo(() => {
		if (form.mode === "allow") {
			return "Only clicks from the selected systems will be tracked.";
		}
		return "Clicks from the selected systems will be blocked.";
	}, [form.mode]);

	const effectiveCount = form.selected.length;

	const busy = saveMutation.isPending || deleteMutation.isPending;

	// ---------------- Handlers ----------------
	function updateCampaignId(value: string) {
		setForm((prev) => ({ ...prev, campaignId: value }));
		setHydratedFor(null);
		if (errors.campaignId) {
			setErrors((prev) => {
				const { campaignId: _drop, ...rest } = prev;
				return rest;
			});
		}
	}

	function toggleOs(code: FormOsCode) {
		setForm((prev) => {
			const present = prev.selected.includes(code);
			return {
				...prev,
				selected: present
					? prev.selected.filter((c) => c !== code)
					: [...prev.selected, code],
			};
		});
		if (errors.selected) {
			setErrors((prev) => {
				const { selected: _drop, ...rest } = prev;
				return rest;
			});
		}
	}

	function setMode(mode: TargetingMode) {
		setForm((prev) => ({ ...prev, mode }));
	}

	function setMinVersion(value: string) {
		setForm((prev) => ({ ...prev, minVersion: value }));
		if (errors.minVersion) {
			setErrors((prev) => {
				const { minVersion: _drop, ...rest } = prev;
				return rest;
			});
		}
	}

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = osTargetingFormSchema.safeParse({
			campaignId: form.campaignId,
			mode: form.mode,
			selected: form.selected,
			minVersion: form.minVersion,
		});
		if (!parsed.success) {
			setErrors(parsed.errors);
			toast.error("Please fix the highlighted fields.");
			return;
		}
		setErrors({});
		saveMutation.mutate({
			campaignId: parsed.data.campaignId,
			mode: parsed.data.mode,
			selected: parsed.data.selected,
			minVersion: parsed.data.minVersion,
		});
	}

	function handleClear() {
		if (!trimmedCampaignId) {
			toast.error("Enter a campaign ID first.");
			return;
		}
		deleteMutation.mutate(trimmedCampaignId);
	}

	// ---------------- Render ----------------
	return (
		<form
			onSubmit={handleSubmit}
			className={`${CARD} flex flex-col gap-6 p-6`}
			noValidate
		>
			<CampaignField
				value={form.campaignId}
				onChange={updateCampaignId}
				error={errors.campaignId}
				loading={query.isFetching}
				locked={Boolean(initialCampaignId)}
			/>

			<ModeField
				mode={form.mode}
				onChange={setMode}
				description={modeDescription}
			/>

			<OsChipsField
				selected={form.selected}
				onToggle={toggleOs}
				error={errors.selected}
			/>

			<MinVersionField
				value={form.minVersion}
				onChange={setMinVersion}
				error={errors.minVersion}
			/>

			<Footer
				busy={busy}
				saveLabel={saveMutation.isPending ? "Saving." : "Save targeting"}
				onClear={handleClear}
				canClear={canQuery}
				effectiveCount={effectiveCount}
				mode={form.mode}
			/>
		</form>
	);
}

// ---------------------------------------------------------------------------
// Field components — broken out for readability but colocated by design.
// ---------------------------------------------------------------------------

function CampaignField(props: {
	value: string;
	onChange: (v: string) => void;
	error?: string;
	loading?: boolean;
	locked?: boolean;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor="os-targeting-campaign-id" className={LABEL}>
				Campaign ID
			</label>
			<input
				id="os-targeting-campaign-id"
				name="campaignId"
				type="text"
				autoComplete="off"
				placeholder="e.g. camp_14ab9f"
				className={INPUT}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				disabled={props.locked}
				aria-invalid={Boolean(props.error)}
				aria-describedby={
					props.error ? "os-targeting-campaign-id-error" : undefined
				}
			/>
			<div className="flex items-center justify-between">
				<p className={HINT}>
					Identifier of the campaign you want to constrain by OS.
				</p>
				{props.loading && (
					<span className={HINT} aria-live="polite">
						Loading existing rule.
					</span>
				)}
			</div>
			{props.error && (
				<p id="os-targeting-campaign-id-error" className={ERROR_TEXT}>
					{props.error}
				</p>
			)}
		</div>
	);
}

function ModeField(props: {
	mode: TargetingMode;
	onChange: (m: TargetingMode) => void;
	description: string;
}) {
	return (
		<fieldset className="flex flex-col gap-2">
			<legend className={LABEL}>Mode</legend>
			<div
				role="radiogroup"
				aria-label="Targeting mode"
				className="inline-flex rounded-[6px] border border-zinc-200 bg-zinc-50 p-0.5"
			>
				<ModeButton
					active={props.mode === "allow"}
					onClick={() => props.onChange("allow")}
					label="Allow list"
				/>
				<ModeButton
					active={props.mode === "deny"}
					onClick={() => props.onChange("deny")}
					label="Deny list"
				/>
			</div>
			<p className={HINT}>{props.description}</p>
		</fieldset>
	);
}

function ModeButton(props: {
	active: boolean;
	onClick: () => void;
	label: string;
}) {
	const base =
		"rounded-[6px] px-3 py-1.5 text-sm font-medium transition-colors";
	const active =
		"bg-white text-zinc-900 shadow-[0_1px_2px_0_rgb(24_24_27/0.06)]";
	const inactive = "text-zinc-600 hover:text-zinc-900";
	return (
		<button
			type="button"
			role="radio"
			aria-checked={props.active}
			onClick={props.onClick}
			className={`${base} ${props.active ? active : inactive}`}
		>
			{props.label}
		</button>
	);
}

function OsChipsField(props: {
	selected: FormOsCode[];
	onToggle: (c: FormOsCode) => void;
	error?: string;
}) {
	return (
		<fieldset className="flex flex-col gap-2">
			<legend className={LABEL}>Operating systems</legend>
			<div className="flex flex-wrap gap-2" role="group" aria-label="Operating systems">
				{FORM_OS_CODES.map((code) => {
					const active = props.selected.includes(code);
					return (
						<OsChip
							key={code}
							code={code}
							active={active}
							onToggle={() => props.onToggle(code)}
						/>
					);
				})}
			</div>
			{props.error ? (
				<p className={ERROR_TEXT}>{props.error}</p>
			) : (
				<p className={HINT}>
					Tap to include or exclude. Selection applies to the active mode above.
				</p>
			)}
		</fieldset>
	);
}

function OsChip(props: {
	code: FormOsCode;
	active: boolean;
	onToggle: () => void;
}) {
	const base =
		"inline-flex items-center gap-2 rounded-[6px] border px-3 py-1.5 text-sm font-medium transition-colors";
	const on = "border-emerald-500 bg-emerald-50 text-emerald-700";
	const off = "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300";
	return (
		<button
			type="button"
			aria-pressed={props.active}
			onClick={props.onToggle}
			className={`${base} ${props.active ? on : off}`}
		>
			<span
				aria-hidden="true"
				className={`h-1.5 w-1.5 rounded-full ${
					props.active ? "bg-emerald-500" : "bg-zinc-300"
				}`}
			/>
			{OS_LABELS[props.code]}
		</button>
	);
}

function MinVersionField(props: {
	value: string;
	onChange: (v: string) => void;
	error?: string;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor="os-targeting-min-version" className={LABEL}>
				Minimum version <span className="font-normal text-zinc-400">(optional)</span>
			</label>
			<input
				id="os-targeting-min-version"
				name="minVersion"
				type="text"
				autoComplete="off"
				placeholder="e.g. 14.0"
				className={`${INPUT} max-w-xs`}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				aria-invalid={Boolean(props.error)}
				aria-describedby={
					props.error ? "os-targeting-min-version-error" : undefined
				}
			/>
			<p className={HINT}>
				Optional floor for the OS version. Leave empty to accept any version.
			</p>
			{props.error && (
				<p id="os-targeting-min-version-error" className={ERROR_TEXT}>
					{props.error}
				</p>
			)}
		</div>
	);
}

function Footer(props: {
	busy: boolean;
	saveLabel: string;
	onClear: () => void;
	canClear: boolean;
	effectiveCount: number;
	mode: TargetingMode;
}) {
	const summary =
		props.effectiveCount === 0
			? "No systems selected yet."
			: `${props.effectiveCount} system${
					props.effectiveCount === 1 ? "" : "s"
				} on the ${props.mode === "allow" ? "allow" : "deny"} list.`;
	return (
		<div className="flex flex-col gap-4 border-t border-zinc-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
			<p className={HINT}>{summary}</p>
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={props.onClear}
					disabled={!props.canClear || props.busy}
					className="rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Clear rule
				</button>
				<button
					type="submit"
					disabled={props.busy}
					className="rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{props.saveLabel}
				</button>
			</div>
		</div>
	);
}

export default OsTargetingForm;
